import React, { useContext, useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Flex, Typography, message } from "antd";
import { AppContext } from "../../../context/AppContext";
import CommonMessageTextArea from "../../../components/main/CommonMessageTextArea";
import useHttp from "../../../hooks/useHttp";
import { mapToMediaList } from "../../../pages/utils/commonUtils";
import type { ActionNodeData } from "../nodes/ActionNode";
import HCheckBoxGroup from "../../../voice/components/common/HCheckBoxGroup";
import { useWorkflowStore } from "../store";
import {
    getTemplateMetaFromPreviewPayload,
    previewHasMergeableContent,
    type TemplatePreviewResponse,
} from "./templatePreviewTypes";
import {
    getWorkflowApiErrorMessage,
    isWorkflowApiLogicalFailure,
    parseFetchDataEnvelope,
} from "../utils/workflowApiResponse";
import { useTemplateStore } from "../../templates/templateStore";

const { Text } = Typography;

const noop = (): void => undefined;

function fileIsPending(f: unknown): boolean {
    return (f as { status?: string }).status !== "done";
}

function mapPreviewMediaToPersistable(
    mediaList: unknown[],
    toPersistableMedia: (items: unknown[]) => unknown[]
): unknown[] {
    return toPersistableMedia(
        mediaList.map((f) => {
            const item = f as Record<string, unknown>;
            return {
                url: item?.url,
                name: item?.name ?? "file",
                size: item?.size ?? -1,
                contentType: (item?.contentType as string) ?? (item?.type as string) ?? "dummy/dummy",
            };
        })
    );
}

export interface AutomatedResponseFormProps {
    data: ActionNodeData;
    update: (d: Partial<ActionNodeData>) => void;
    /** With `onApplyOnceDraftChange`, the checkbox is draft-only until Save Changes / Apply. */
    applyOnceDraft?: boolean;
    onApplyOnceDraftChange?: (value: boolean) => void;
    onStepFormBusyChange?: (busy: boolean) => void;
    nodeId: string;
}


function buildPreviewFromPersisted(params: {
    templateId?: string;
    components: unknown;
    message?: string;
    fieldNameAndValueMap?: Record<string, unknown>;
    mediaFileList?: unknown;
    templateName?: string;
    templateIntegId?: string;
}): TemplatePreviewResponse | null {
    const { templateId, components, message, fieldNameAndValueMap, mediaFileList, templateName, templateIntegId } = params;
    if (!templateId) return null;
    if (components === undefined || components === null) return null;
    if (Array.isArray(components) && components.length === 0) return null;
    if (
        typeof components === "object" &&
        !Array.isArray(components) &&
        Object.keys(components as Record<string, unknown>).length === 0
    ) {
        return null;
    }
    return {
        components,
        data: typeof message === "string" ? message : undefined,
        fieldNameAndValueMap,
        mediaFileList: mediaFileList as unknown[] | undefined,
        templateId,
        templateName,
        templateIntegId,
    };
}

export const AutomatedResponseForm: React.FC<AutomatedResponseFormProps> = ({
    data,
    update,
    applyOnceDraft,
    onApplyOnceDraftChange,
    onStepFormBusyChange,
    nodeId,
}) => {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const storeTemplates = useTemplateStore((s) => s.templates);
    const applyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);
    const setApplyOncePerContact = useWorkflowStore((s) => s.setApplyOncePerContact);
    const isApplyOnceDraftControlled =
        onApplyOnceDraftChange != null && typeof applyOnceDraft === "boolean";

    const [fileList, setFileList] = useState<unknown[]>(
        () => mapToMediaList(data.mediaFileList ?? [], false)
    );

    const lastPersistedMediaRef = useRef<unknown>(data.mediaFileList);

    const updateWithMediaTracking = useCallback(
        (patch: Partial<ActionNodeData>) => {
            if (Object.prototype.hasOwnProperty.call(patch, "mediaFileList")) {
                lastPersistedMediaRef.current = patch.mediaFileList;
            }
            update(patch);
        },
        [update]
    );

    const [previewResponse, setPreviewResponse] = useState<TemplatePreviewResponse | null>(() =>
        buildPreviewFromPersisted({
            templateId: data.templateId,
            components: data.components,
            message: data.message,
            fieldNameAndValueMap: data.fieldNameAndValueMap,
            mediaFileList: data.mediaFileList,
            templateName: data.templateName,
            templateIntegId: data.templateIntegId,
        })
    );
    const [templateId, setTemplateId] = useState<string | undefined>(data.templateId);
    const [shortcutTemplateOpen, setShortcutTemplateOpen] = useState(false);
    const [iconTemplateOpen, setIconTemplateOpen] = useState(false);
    const [messageAreaLoading, setMessageAreaLoading] = useState(false);
    const messageTextAreaContentRef = useRef<HTMLDivElement>(null);
    const latestTemplateIdRef = useRef<string | undefined>(data.templateId);
    const messageFallbackRef = useRef<string | undefined>(data.message);

    const isUploadBusy = useMemo(() => fileList.some(fileIsPending), [fileList]);
    const isFormBusy = isUploadBusy || messageAreaLoading;

    useEffect(() => {
        onStepFormBusyChange?.(isFormBusy);
        return () => { onStepFormBusyChange?.(false); };
    }, [isFormBusy, onStepFormBusyChange]);

    const toPersistableMedia = useCallback((items: unknown[]) => {
        return items.map((f) => {
            const item = f as Record<string, unknown>;
            return {
                url: item?.url as string | undefined,
                name: (item?.name as string) ?? "file",
                size: (item?.size as number) ?? -1,
                contentType: (item?.contentType as string) ?? (item?.type as string) ?? "dummy/dummy",
                thumbnailUrl: (item?.thumbnailUrl as string) ?? (item?.url as string),
            };
        });
    }, []);

    useEffect(() => {
        if (data.mediaFileList === lastPersistedMediaRef.current) return;
        if (fileList.some(fileIsPending)) return;
        lastPersistedMediaRef.current = data.mediaFileList;
        setFileList(mapToMediaList(data.mediaFileList ?? [], false));
    }, [data.mediaFileList, fileList]);

    useEffect(() => {
        const fromPersisted = buildPreviewFromPersisted({
            templateId: data.templateId,
            components: data.components,
            message: messageFallbackRef.current,
            fieldNameAndValueMap: data.fieldNameAndValueMap,
            mediaFileList: data.mediaFileList,
            templateName: data.templateName,
            templateIntegId: data.templateIntegId,
        });
        if (fromPersisted) {
            setPreviewResponse(fromPersisted);
        } else if (!data.templateId) {
            setPreviewResponse(null);
        }
    }, [
        nodeId,
        data.templateId,
        data.components,
        data.fieldNameAndValueMap,
        data.mediaFileList,
        data.templateName,
        data.templateIntegId,
    ]);

    const isFirstFileListRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstFileListRenderRef.current) {
            isFirstFileListRenderRef.current = false;
            return;
        }
        if (fileList.some(fileIsPending)) return;
        updateWithMediaTracking({
            hasMedia: fileList.length > 0,
            mediaFileList: toPersistableMedia(fileList),
        });
    }, [fileList, updateWithMediaTracking, toPersistableMedia]);

    const resolvedTemplateName = useMemo(() => {
        if (data.templateName?.trim()) return data.templateName.trim();
        if (!data.templateId) return undefined;
        const match = (storeTemplates as Array<{ templateId: string; friendlyName?: string }>).find(
            (t) => t.templateId === data.templateId
        );
        return match?.friendlyName?.trim() || undefined;
    }, [data.templateName, data.templateId, storeTemplates]);

    useEffect(() => {
        if (data.templateId && !data.templateName && resolvedTemplateName) {
            update({ templateName: resolvedTemplateName });
        }
    }, [data.templateId, data.templateName, resolvedTemplateName, update]);

    useEffect(() => {
        latestTemplateIdRef.current = data.templateId;
        messageFallbackRef.current = data.message;
        if (data.templateId !== undefined) setTemplateId(data.templateId);
    }, [data.templateId, data.message]);

    const handleMessage = useCallback(
        (msg: string, _event?: unknown, source?: string) => {
            if (source === "templates") {
                update({ message: msg });
                return;
            }
            if (data.templateId && !data.components && !previewResponse?.components) {
                if (msg === (data.message ?? "")) return;
                setPreviewResponse(null);
                setTemplateId(undefined);
                setFileList([]);
                updateWithMediaTracking({
                    message: msg,
                    templateId: undefined,
                    templateName: undefined,
                    templateIntegId: undefined,
                    components: undefined,
                    fieldNameAndValueMap: undefined,
                    hasMedia: false,
                    mediaFileList: [],
                });
                return;
            }
            update({ message: msg });
        },
        [data.templateId, data.message, data.components, previewResponse?.components, update, updateWithMediaTracking]
    );

    const handlePreviewResponse = useCallback(
        (response: TemplatePreviewResponse | null) => {
            setPreviewResponse(response);
            if (response == null) return;

            const mediaList = Array.isArray(response.mediaFileList) ? response.mediaFileList : [];
            setFileList(mapToMediaList(mediaList, false));

            const contentPatch: Partial<ActionNodeData> = {};
            if (typeof response.data === "string") contentPatch.message = response.data;
            if (response.components !== undefined) contentPatch.components = response.components;
            if (response.fieldNameAndValueMap !== undefined) {
                contentPatch.fieldNameAndValueMap = response.fieldNameAndValueMap;
            }

            const meta = getTemplateMetaFromPreviewPayload(response as Record<string, unknown>);
            if (meta) {
                update({
                    ...contentPatch,
                    templateId: meta.templateId,
                    ...(meta.templateName ? { templateName: meta.templateName } : {}),
                    templateIntegId: meta.templateIntegId,
                    message: typeof response.data === "string" ? response.data : contentPatch.message ?? "",
                });
                return;
            }
            if (previewHasMergeableContent(response)) update(contentPatch);
        },
        [update]
    );

    const handlePreviewRemove = useCallback(() => {
        setPreviewResponse(null);
        setTemplateId(undefined);
        setFileList([]);
        updateWithMediaTracking({
            message: "",
            templateId: undefined,
            templateName: undefined,
            templateIntegId: undefined,
            hasMedia: false,
            mediaFileList: [],
            components: undefined,
            fieldNameAndValueMap: undefined,
        });
    }, [updateWithMediaTracking]);

    /**
     * Called by CommonMessageTextArea when the file list changes (upload start, finish, or remove).
     * Only updates local state — the `fileList` effect persists settled media to the draft.
     * This is the key invariant: we never write to the draft while a file is still uploading,
     * so `data.mediaFileList` never contains an `uploading` entry that would get synced back
     * and clobber the local upload state.
     */
    const handleMediaFileList = useCallback(
        (list: unknown[] | ((prev: unknown[]) => unknown[])) => {
            setFileList(typeof list === "function" ? list : list);
        },
        []
    );

    useEffect(() => {
        const tid = data.templateId;
        if (!tid || previewResponse?.components || !appProperties) return;
        const integId = data.templateIntegId ?? appProperties?.randomIntegId;
        if (!integId) return;

        const requestTemplateId = tid;
        let effectActive = true;

        setMessageAreaLoading(true);
        fetchData(
            `omessage/${integId}/template/${requestTemplateId}/preview?associatedObjectId=&associatedObjectType=&phone=`,
            "GET",
            null,
            appProperties
        )
            .then((response) => {
                if (!effectActive || latestTemplateIdRef.current !== requestTemplateId) return;
                if (typeof response !== "string") return;
                const env = parseFetchDataEnvelope(response);
                if (isWorkflowApiLogicalFailure(env.axiosBody) || isWorkflowApiLogicalFailure(env.entity)) {
                    message.error(
                        getWorkflowApiErrorMessage(env.entity, env.axiosBody) ??
                            "Could not load template preview."
                    );
                    return;
                }
                const previewRoot = env.entity ?? env.axiosBody;
                if (!previewRoot || typeof previewRoot !== "object") return;
                const preview = previewRoot as Record<string, unknown>;
                if (!(preview?.data || preview?.mediaFileList || preview?.components)) return;
                if (latestTemplateIdRef.current !== requestTemplateId) return;

                const mediaList = Array.isArray(preview?.mediaFileList) ? preview.mediaFileList : [];
                setPreviewResponse(preview as TemplatePreviewResponse);
                setFileList(mapToMediaList(mediaList, false));

                const persistableMedia = mapPreviewMediaToPersistable(mediaList, toPersistableMedia);
                const meta = getTemplateMetaFromPreviewPayload(preview);
                const contentPatch: Partial<ActionNodeData> = {
                    message: (preview?.data as string | undefined) ?? messageFallbackRef.current ?? "",
                    hasMedia: mediaList.length > 0,
                    mediaFileList: persistableMedia,
                    components: preview?.components,
                    fieldNameAndValueMap: preview?.fieldNameAndValueMap as Record<string, unknown> | undefined,
                };

                if (meta) {
                    updateWithMediaTracking({
                        ...contentPatch,
                        templateId: meta.templateId,
                        ...(meta.templateName ? { templateName: meta.templateName } : {}),
                        templateIntegId: meta.templateIntegId,
                    });
                } else if (previewHasMergeableContent(preview as TemplatePreviewResponse)) {
                    updateWithMediaTracking(contentPatch);
                }
            })
            .catch(() => undefined)
            .finally(() => {
                if (effectActive) setMessageAreaLoading(false);
            });

        return () => { effectActive = false; };
    }, [data.templateId, data.templateIntegId, appProperties, fetchData, previewResponse?.components, toPersistableMedia, updateWithMediaTracking]);

    const messageForTextArea = useMemo(() => data.message ?? "", [data.message]);

    if (!appProperties) return null;

    const commonMessageProps = {
        message: messageForTextArea,
        handleMessage,
        handleMedia: noop,
        handleMediaFileList,
        handlePreviewResponse,
        appProperties,
        fileList,
        source: "automation" as const,
        integId: appProperties?.randomIntegId,
        moduleName: "",
        fetchAllTemplates: true,
        messageTextAreaContentRef,
        templateId,
        setTemplateId,
        previewResponse,
        shortcutTemplateOpen,
        setShortcutTemplateOpen,
        iconTemplateOpen,
        setIconTemplateOpen,
        chatView: false,
        textAreaMinRows: 4,
        fetchAndShowTemplatePreview: true,
        hiddenItems: [] as string[],
        messageAreaLoading,
        setMessageAreaLoading,
        handleManageSubscription: noop,
        toggleDrawer: noop,
        handlePreviewRemove,
        renderEmojiPickerDownward: true,
        isContentPresent: !!(
            data.templateId ||
            data.message ||
            fileList.length > 0 ||
            previewResponse?.components ||
            data.components
        ),
        filesPreviewPosition: "bottom",
    };

    return (
        <Flex vertical gap="small">
            <Text className="trigger-rule-config-panel__label hs-fw-500 hs-fs-12 hs-color-BlackRussian">Message</Text>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <CommonMessageTextArea {...(commonMessageProps as any)} />
            <div className="hs-fs-12">
                <HCheckBoxGroup
                    options={[{ label: "Apply once per contact", value: "true" }]}
                    checkedValue={
                        (isApplyOnceDraftControlled ? applyOnceDraft : (applyOncePerContact ?? false))
                            ? "true"
                            : "false"
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const checked = (e.target as HTMLInputElement).checked;
                        if (isApplyOnceDraftControlled) {
                            onApplyOnceDraftChange(checked);
                        } else {
                            setApplyOncePerContact(checked);
                        }
                    }}
                    optionsFontSize={12}
                />
                <Text className="hs-fs-12 hs-color-mediumDark d-block mt-1">
                    When enabled, this rule runs only once per contact.
                </Text>
            </div>
        </Flex>
    );
};
